import { inject, Injectable, NgZone } from '@angular/core';
import { environment } from '../../environments/environment';
import {
  AtlasContext,
  ForgeEventHandlers,
} from '../dashboard/ui-editor/blockly-interface/helpers/forge.types';
import { AuthService } from './auth.service';
import { SessionGuardService } from './session-guard.service';
import { reasoningHeader } from './reasoning-header.util';
import { ClientService } from './client.service';

@Injectable({
  providedIn: 'root',
})
export class ForgeService {
  private zone = inject(NgZone);
  private authService = inject(AuthService);
  private sessionGuard = inject(SessionGuardService);
  private client = inject(ClientService);

  /**
   * `pageContext` IS RETIRED — do not add it back (2026-08-05).
   *
   * The CF retired it on 2026-08-01 and refuses a body carrying it BY NAME:
   *   "pageContext is retired — Forge takes an atlasContext (v3). Run Atlas selection first and send its
   *    hydrated payload."
   * This service kept sending it for four days, so every Forge run from the UI died at the door before a
   * token was generated. Removing the parameter from the SIGNATURE — rather than ignoring the field — is
   * what makes that unrepeatable: a caller that still has a PageContext now fails to compile instead of
   * failing at runtime, and the compiler names every site.
   *
   * PageContext is NOT dead in the editor. It stays the vocabulary for everything LOCAL — `formatPlanAsText`,
   * `extractVariablesFromSteps`, `loadStepsToWorkspace`. It is dead on the WIRE only. Those are different
   * questions and conflating them is what kept the field here.
   *
   * `atlasContext` comes from `AtlasAgentService.runSelection(...).context`. There is no fallback: a run
   * without a selection has nothing to send, and inventing an empty AtlasContext would put the failure
   * inside Forge's reasoning instead of in front of the user.
   */
  streamPlan(
    body: { prompt: string; functionName: string; atlasContext: AtlasContext },
    handlers: ForgeEventHandlers,
    signal: AbortSignal
  ): void {
    this.stream(
      `${environment.functionsBaseUrl}/forgeGenerate`,
      { ...body, mode: 'new' },
      handlers,
      signal
    );
  }

  /** Same contract as `streamPlan` — see the note there on why `pageContext` is gone from the signature. */
  streamModify(
    body: {
      prompt: string;
      currentSummary: string;
      atlasContext: AtlasContext;
    },
    handlers: ForgeEventHandlers,
    signal: AbortSignal
  ): void {
    this.stream(
      `${environment.functionsBaseUrl}/forgeGenerate`,
      { ...body, mode: 'modify' },
      handlers,
      signal
    );
  }

  private async stream(
    url: string,
    body: object,
    handlers: ForgeEventHandlers,
    signal: AbortSignal
  ): Promise<void> {
    const token = await this.authService.getAuthToken();
    if (!token) {
      handlers.onError?.('Not authenticated');
      return;
    }
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Session-Id': this.sessionGuard.getSessionId(),
        ...reasoningHeader(),
        // Which client this work is for. The CF cannot infer it: ForgeRequest has never carried a
        // clientId, so without this the AI call log files the trace under the builder's uid instead.
        'X-Zavo-Client-Id': this.client.selectedClientId,
      },
      body: JSON.stringify(body),
      signal,
    })
      .then(async (res) => {
        if (!res.ok || !res.body) {
          handlers.onError?.(`HTTP ${res.status}`);
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        // Did the stream ever reach a conclusion? `dispatch` only calls a handler for an event it
        // recognises, so a stream that closes after `progress` events — a CF crash, a dropped
        // connection, a 200 with an empty body — calls NOTHING. The interactive UI merely spun
        // forever on that; a caller awaiting a Promise deadlocks, and a batch loop deadlocks with it.
        let settled = false;
        const terminal: ForgeEventHandlers = {
          ...handlers,
          onPlan: (...a) => { settled = true; handlers.onPlan?.(...a); },
          onError: (...a) => { settled = true; handlers.onError?.(...a); },
          onContextRequest: (...a) => { settled = true; handlers.onContextRequest?.(...a); },
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const parts = buffer.split('\n\n');
          buffer = parts.pop() ?? '';

          for (const part of parts) {
            let eventType = 'message';
            let dataLine = '';
            for (const line of part.split('\n')) {
              if (line.startsWith('event: '))
                eventType = line.slice(7).trim();
              else if (line.startsWith('data: '))
                dataLine = line.slice(6).trim();
            }
            if (!dataLine) continue;
            try {
              const parsed = JSON.parse(dataLine);
              this.zone.run(() => this.dispatch(eventType, parsed, terminal));
            } catch {
              /* malformed SSE event — skip */
            }
          }
        }
        // Reached only on a CLEAN close — an abort throws out of read() into .catch below, so a
        // cancelled run never lands here and is never reported as a failure.
        if (!settled) {
          this.zone.run(() => handlers.onError?.('The Forge stream closed without returning a plan.'));
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          handlers.onError?.(err.message ?? 'Unknown error');
        }
      });
  }

  private dispatch(
    eventType: string,
    data: any,
    handlers: ForgeEventHandlers
  ): void {
    switch (eventType) {
      case 'progress':
        handlers.onProgress?.(data.phase, data.message);
        break;
      case 'plan':
        handlers.onPlan?.(data.steps, data.warnings);
        break;
      case 'error':
        handlers.onError?.(data.message);
        break;
      // ── The two events the CF has always sent and this side has always dropped (2026-08-05) ──────────
      // `context_request` is TERMINAL: the CF returns straight after sending it, so no plan follows. That
      // is exactly why dropping it was so bad — the stream closed with no plan and no error, and a caller
      // waiting on onPlan/onError waited forever. See docs/plans/forge-atlas-ask-back-loop.md.
      case 'context_request':
        handlers.onContextRequest?.(data.requests ?? [], data.plan ?? '', {
          rejectedSteps: data.rejectedSteps,
          validationErrors: data.validationErrors,
        });
        break;
      // `validation` is NOT terminal — the CF sends one per repair round (attempt 0, then 1..N) and a plan
      // can still arrive after it. A handler here should report progress, never conclude.
      case 'validation':
        handlers.onValidation?.(data.errors ?? [], data.attempt ?? 0);
        break;
      // NO default, deliberately. A future event this build does not know is not an error — the CF and the
      // editor deploy separately, so an unknown type means "newer backend", and turning that into an
      // onError would break a working stream. Silence here is safe BECAUSE every terminal path above is
      // now handled; that was not true before, which is what made the silent drop fatal rather than inert.
    }
  }
}
